'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ProductKindBadge,
  VERTICAL_PRODUCT_KIND_OPTIONS as PRODUCT_KIND_OPTIONS,
  isValidProductKind,
  productKindDescriptionHint,
  productKindRequiresDescription,
  type VerticalProductKind as ProductKind,
} from '@/config/marketplaceExtensions';
import {
  createVendorProduct,
  deleteVendorProduct,
  formatPrice,
  listShippingProfiles,
  listVendorProducts,
  updateVendorProduct,
  type ProductStatus,
  type VendorProduct,
  type VendorShippingProfile,
} from './api';
import { ProductImageGalleryEditor } from './ProductImageGalleryEditor';

export interface VendorProductsPanelProps {
  vendorUserId: string;
  readOnly?: boolean;
}

const emptyProduct = (): Omit<VendorProduct, 'id' | 'createdAt' | 'updatedAt'> & {
  productKind?: ProductKind;
} => ({
  name: '',
  description: '',
  sku: '',
  priceCents: 0,
  currency: 'USD',
  trackInventory: false,
  inventoryQty: undefined,
  productKind: undefined,
  status: 'draft',
  weightGrams: undefined,
  shippingProfileId: undefined,
  shippingSurchargeCents: undefined,
});

export function VendorProductsPanel({ vendorUserId, readOnly = false }: VendorProductsPanelProps) {
  const [products, setProducts] = useState<VendorProduct[]>([]);
  const [shippingProfiles, setShippingProfiles] = useState<VendorShippingProfile[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyProduct());
  const [galleryPaths, setGalleryPaths] = useState<string[]>([]);
  const [galleryUrls, setGalleryUrls] = useState<string[] | undefined>();
  const [priceInput, setPriceInput] = useState('0.00');
  const [error, setError] = useState('');

  const descriptionHint = useMemo(
    () => productKindDescriptionHint(form.productKind),
    [form.productKind],
  );
  const descriptionRequired = productKindRequiresDescription(form.productKind);

  useEffect(() => {
    listVendorProducts(vendorUserId)
      .then((r) => setProducts(r.products))
      .catch(() => setProducts([]));
    listShippingProfiles(vendorUserId)
      .then((r) => setShippingProfiles(r.profiles.filter((p) => p.active)))
      .catch(() => setShippingProfiles([]));
  }, [vendorUserId]);

  function resetForm() {
    setEditingId(null);
    setForm(emptyProduct());
    setGalleryPaths([]);
    setGalleryUrls(undefined);
    setPriceInput('0.00');
    setError('');
  }

  function startEdit(product: VendorProduct) {
    setEditingId(product.id);
    setForm({
      name: product.name,
      description: product.description ?? '',
      sku: product.sku ?? '',
      priceCents: product.priceCents,
      currency: product.currency,
      trackInventory: product.trackInventory,
      inventoryQty: product.inventoryQty,
      productKind: isValidProductKind(product.productKind) ? product.productKind : undefined,
      status: product.status,
      weightGrams: product.weightGrams,
      shippingProfileId: product.shippingProfileId,
      shippingSurchargeCents: product.shippingSurchargeCents,
    });
    setGalleryPaths(product.imageObjectPaths ?? []);
    setGalleryUrls(product.imageUrls);
    setPriceInput((product.priceCents / 100).toFixed(2));
  }

  async function saveProduct(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValidProductKind(form.productKind)) {
      setError('Select a product type');
      return;
    }
    if (descriptionRequired && !(form.description ?? '').trim()) {
      setError('Description is required for this product type');
      return;
    }
    const priceCents = Math.round(Number(priceInput) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 0) {
      setError('Enter a valid price');
      return;
    }
    const payload = {
      ...form,
      priceCents,
      inventoryQty: form.trackInventory ? form.inventoryQty ?? 0 : undefined,
    };
    try {
      if (editingId) {
        const updated = await updateVendorProduct(vendorUserId, editingId, payload);
        setProducts((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
        startEdit(updated);
      } else {
        const created = await createVendorProduct(vendorUserId, payload);
        setProducts((prev) => [created, ...prev]);
        startEdit(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save product');
    }
  }

  async function removeProduct(productId: string) {
    await deleteVendorProduct(vendorUserId, productId);
    setProducts((prev) => prev.filter((p) => p.id !== productId));
    if (editingId === productId) resetForm();
  }

  async function setStatus(product: VendorProduct, status: ProductStatus) {
    const updated = await updateVendorProduct(vendorUserId, product.id, { status });
    setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
  }

  return (
    <section>
      <h2 className="vendor-console__section-title">Products</h2>
      <p className="vendor-console__hint">
        Add items with a product type and description so buyers know what they are getting. Paydirt bags
        also show community haul averages when linked haul reports exist.
      </p>
      {error && <p className="form-error">{error}</p>}

      {!readOnly && (
      <form onSubmit={(e) => void saveProduct(e)} className="vendor-console__form-grid feed-card settings-page">
        <h3>{editingId ? 'Edit product' : 'Add product'}</h3>
        <label>
          Name
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </label>
        <label>
          Product type
          <select
            value={form.productKind ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                productKind: e.target.value ? (e.target.value as ProductKind) : undefined,
              })
            }
            required
          >
            <option value="" disabled>
              Select product type…
            </option>
            {PRODUCT_KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Description{descriptionRequired ? ' (required)' : ''}
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            required={descriptionRequired}
            placeholder={descriptionHint}
          />
          <span className="text-muted vendor-console__hint">{descriptionHint}</span>
        </label>
        <label>
          SKU
          <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
        </label>
        <label>
          Price (USD)
          <input
            type="number"
            min="0"
            step="0.01"
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            required
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={form.trackInventory}
            onChange={(e) => setForm({ ...form, trackInventory: e.target.checked })}
          />{' '}
          Track inventory
        </label>
        {form.trackInventory && (
          <label>
            Quantity in stock
            <input
              type="number"
              min="0"
              value={form.inventoryQty ?? 0}
              onChange={(e) => setForm({ ...form, inventoryQty: Number(e.target.value) })}
            />
          </label>
        )}
        <label>
          Weight (grams)
          <input
            type="number"
            min="0"
            value={form.weightGrams ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                weightGrams: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            placeholder="Required for weight-based shipping"
          />
        </label>
        <label>
          Shipping profile
          <select
            value={form.shippingProfileId ?? ''}
            onChange={(e) =>
              setForm({
                ...form,
                shippingProfileId: e.target.value || undefined,
              })
            }
          >
            <option value="">Store default</option>
            {shippingProfiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Shipping surcharge (USD)
          <input
            type="number"
            min="0"
            step="0.01"
            value={((form.shippingSurchargeCents ?? 0) / 100).toFixed(2)}
            onChange={(e) =>
              setForm({
                ...form,
                shippingSurchargeCents: Math.round(Number(e.target.value) * 100) || undefined,
              })
            }
          />
        </label>
        <label>
          Status
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as ProductStatus })}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </label>
        {editingId && (
          <ProductImageGalleryEditor
            vendorUserId={vendorUserId}
            productId={editingId}
            imageObjectPaths={galleryPaths}
            imageUrls={galleryUrls}
            readOnly={readOnly}
            onChange={(paths, urls) => {
              setGalleryPaths(paths);
              setGalleryUrls(urls);
              setProducts((prev) =>
                prev.map((p) =>
                  p.id === editingId ? { ...p, imageObjectPaths: paths, imageUrls: urls } : p,
                ),
              );
            }}
          />
        )}
        <div className="vendor-console__submit settings-page__submit">
          {editingId && (
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-primary">
            {editingId ? 'Save product' : 'Add product'}
          </button>
        </div>
      </form>
      )}

      <div className="vendor-products__list">
        {products.map((product) => (
          <article key={product.id} className="vendor-product-card">
            <div className="vendor-product-card__header">
              {product.imageUrls?.[0] && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={product.imageUrls[0]} alt={contentImageAlt(product.name, 'product photo')} className="vendor-product-card__thumb" loading="lazy" />
              )}
              <div>
                <strong>{product.name}</strong>{' '}
                {ProductKindBadge ? <ProductKindBadge kind={product.productKind} /> : null}
                <span
                  className={`vendor-console__badge${
                    product.status === 'active' ? ' vendor-console__badge--active' : ' vendor-console__badge--draft'
                  }`}
                >
                  {product.status}
                </span>
                <p>{formatPrice(product.priceCents, product.currency)}</p>
                {product.stripePriceId && (
                  <p className="text-muted">Listed for checkout (Stripe price synced)</p>
                )}
                {product.weightGrams != null && <p className="text-muted">Weight: {product.weightGrams} g</p>}
                {product.description && <p className="vendor-product-card__description">{product.description}</p>}
                {product.trackInventory && <p>In stock: {product.inventoryQty ?? 0}</p>}
              </div>
              <div className="vendor-console__actions">
                {!readOnly && (
                  <>
                <button type="button" className="btn btn-secondary" onClick={() => startEdit(product)}>
                  Edit
                </button>
                {product.status !== 'active' && (
                  <button type="button" className="btn btn-secondary" onClick={() => void setStatus(product, 'active')}>
                    Publish
                  </button>
                )}
                {product.status === 'active' && (
                  <button type="button" className="btn btn-secondary" onClick={() => void setStatus(product, 'draft')}>
                    Unpublish
                  </button>
                )}
                <button type="button" className="btn btn-link" onClick={() => void removeProduct(product.id)}>
                  Delete
                </button>
                  </>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
